import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Box,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { PDFSection } from '../../services/pdfService';

interface EditableSectionProps {
  section: PDFSection;
  onSave: (sectionId: string, newContent: string) => Promise<void>;
  isEditing?: boolean;
  onEditStart?: (sectionId: string) => void;
  onEditCancel?: () => void;
}

const EditableSection: React.FC<EditableSectionProps> = ({
  section,
  onSave,
  isEditing = false,
  onEditStart,
  onEditCancel
}) => {
  const [editing, setEditing] = useState(isEditing);
  const [content, setContent] = useState(section.content);
  const [saving, setSaving] = useState(false);
  const [hovered, setHovered] = useState(false);
  const textFieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setEditing(isEditing);
  }, [isEditing]);

  useEffect(() => {
    setContent(section.content);
  }, [section.content]);

  const handleCardClick = () => {
    if (!editing) {
      setEditing(true);
      if (onEditStart) {
        onEditStart(section.id);
      }
      // Focus the text field after a short delay
      setTimeout(() => {
        textFieldRef.current?.querySelector('textarea')?.focus();
      }, 100);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(section.id, content);
      setEditing(false);
    } catch (error) {
      console.error('Error saving section:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setContent(section.content); // Reset to original
    setEditing(false);
    if (onEditCancel) {
      onEditCancel();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Save on Ctrl+Enter or Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    // Cancel on Escape
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        cursor: editing ? 'default' : 'pointer',
        transition: 'all 0.2s',
        border: editing ? '2px solid #003366' : '1px solid #e0e0e0',
        '&:hover': {
          boxShadow: editing ? 4 : 2,
          borderColor: editing ? '#003366' : '#003366',
        },
        position: 'relative'
      }}
      onClick={!editing ? handleCardClick : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="button"
      tabIndex={editing ? -1 : 0}
      aria-label={`${section.title}. Click anywhere to edit`}
      onKeyPress={(e) => {
        if (!editing && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      <CardContent>
        {/* Section Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              sx={{
                color: 'primary.main',
                fontWeight: 'bold',
                fontFamily: 'Merriweather, serif',
                mb: 1
              }}
            >
              {section.sectionNumber}. {section.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                label={`Section ${section.sectionNumber}`}
                size="small"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  fontSize: '0.7rem'
                }}
              />
              {section.metadata && (
                <Chip
                  label={`${section.metadata.characterCount.toLocaleString()} chars`}
                  size="small"
                  sx={{
                    bgcolor: '#f0f0f0',
                    color: '#666',
                    fontSize: '0.7rem'
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Action Buttons */}
          {editing ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Save (Ctrl+Enter)">
                <IconButton
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    bgcolor: '#2e7d32',
                    color: 'white',
                    '&:hover': { bgcolor: '#1b5e20' }
                  }}
                  aria-label="Save changes"
                >
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel (Esc)">
                <IconButton
                  onClick={handleCancel}
                  disabled={saving}
                  sx={{
                    bgcolor: 'secondary.main',
                    color: 'white',
                    '&:hover': { bgcolor: '#8B0000' }
                  }}
                  aria-label="Cancel editing"
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            hovered && (
              <Tooltip title="Click anywhere to edit">
                <IconButton
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                  aria-label="Edit section"
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )
          )}
        </Box>

        {/* Content */}
        {editing ? (
          <TextField
            ref={textFieldRef}
            fullWidth
            multiline
            rows={10}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="outlined"
            disabled={saving}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'Source Sans Pro, sans-serif',
                fontSize: '1rem',
                lineHeight: 1.6
              }
            }}
            aria-label={`Edit content for ${section.title}`}
          />
        ) : (
          <Typography
            variant="body1"
            sx={{
              fontFamily: 'Source Sans Pro, sans-serif',
              lineHeight: 1.8,
              whiteSpace: 'pre-wrap',
              color: '#333'
            }}
          >
            {content || '(No content)'}
          </Typography>
        )}

        {/* Edit Hint */}
        {!editing && hovered && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              bgcolor: 'rgba(0, 51, 102, 0.9)',
              color: 'white',
              px: 2,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.8rem'
            }}
          >
            Click anywhere to edit
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EditableSection;
